<script setup lang="ts">
import { Project } from "@/types";
import BasicDetails from "./BasicDetails.vue";
import ProjectDocuments from "./ProjectDocuments.vue";
import TerminTimeline from "./TerminTimeline.vue";
import ProjectAppointments from "./ProjectAppointments.vue";
import ThreeBlockRow from "./ThreeBlockRow.vue";
import { defineProps, defineEmits } from "vue";
import { ref } from "vue";
import InlineLoader from "@/components/Loaders/InlineLoader.vue";
import { getProjectStatusColor } from "@/utils/status-color";

type Props = {
  projectDetails: Project | null;
  isLoading: boolean;
};

const isTimeline = ref(true);
const props = defineProps<Props>();
type Emits = {
  (event: "refresh"): void;
};
defineEmits<Emits>();
</script>

<template>
  <!-- Project overview -->
  <div class="max-w-[1084px] px-3 xl:px-[50px] mx-auto h-full">
    <!-- Header -->
    <div
      class="flex flex-col items-start sm:flex-row sm:items-center justify-between"
    >
      <!-- Left -->
      <div class="flex items-center gap-2 pb-[9px]">
        <h2
          class="text-xl leading-[26px] font-medium text-primary-text-color tracking-[0.01rem]"
        >
          <InlineLoader v-if="isLoading" />
          <span
            v-else
            class="text-xl leading-[26px] font-medium text-primary-text-color tracking-[0.01rem]"
          >
            {{ projectDetails?.name ? projectDetails?.name : "" }}
          </span>
        </h2>
        <InlineLoader v-if="isLoading" />
        <span
          v-else
          class="text-sm tracking-[0.01rem] text-primary-text-color px-[6px] py-[2px] rounded-primary-radius"
          :style="{
            backgroundColor: `${getProjectStatusColor(
              projectDetails?.status?.id
            )}`,
          }"
          >{{ projectDetails ? projectDetails?.status?.name : "" }}</span
        >
      </div>
      <!-- Right -->
      <div v-if="projectDetails?.customerLogo">
        <img
          :src="projectDetails?.customerLogo"
          alt="Customer logo"
          width="84"
        />
      </div>
    </div>
    <!-- Project overview -->
    <div class="h-[calc(100vh-120px)] overflow-y-scroll">
      <div>
        <ThreeBlockRow
          :is-loading="isLoading"
          :customer-name="projectDetails ? projectDetails?.name : ''"
          :cost-carrier-code="
            projectDetails ? projectDetails?.costCarrierCode + '' : ''
          "
          :meister="projectDetails ? projectDetails?.meister : ''"
          :next-termin="projectDetails ? projectDetails.nextTermin : ''"
          :last-termin="projectDetails ? projectDetails.lastTermin : ''"
        />
      </div>
      <div>
        <BasicDetails
          :is-loading="isLoading"
          :project-id="String(projectDetails?.id)"
          :vnb="projectDetails?.vnb"
          :wechselrichter="projectDetails?.wechselrichter"
          :speicher="projectDetails?.speicher"
          :masskonzept="projectDetails?.masskonzept"
          :wb="projectDetails?.wb"
          :number-of-modules="projectDetails?.numberOfModules"
          :modul-type="projectDetails?.modulType"
          :ibn-date="projectDetails?.ibnDate"
          :home-owner-name="projectDetails?.homeownerName"
          :home-owner-address="projectDetails?.homeownerAddress"
          :home-owner-phone="projectDetails?.homeownerPhone"
          :email-address="projectDetails?.ownerEmail"
          :strings="projectDetails?.strings"
          :module-fields="projectDetails?.moduleFields"
          :bauabschnitt="projectDetails?.bauabschnitt"
          :bauabschnittDetails="projectDetails?.bauabschnittDetails"
          @refresh="$emit('refresh')"
        />
      </div>
      <div class="grid grid-cols-1 lg:grid-cols-2 gap-3 mb-5">
        <div>
          <div v-if="isTimeline">
            <TerminTimeline
              :is-loading="isLoading"
              :projectDetails="projectDetails"
              @show-appointments="isTimeline = false"
            />
          </div>
          <div v-if="!isTimeline">
            <ProjectAppointments
              :projectDetails="projectDetails"
              @show-timeline="isTimeline = true"
            />
          </div>
        </div>
        <div>
          <ProjectDocuments
            :is-loading="isLoading"
            :projectDetails="projectDetails"
            @refresh="$emit('refresh')"
          />
        </div>
      </div>
    </div>
  </div>
</template>
